import { resolveConversation, sendUserReply } from 'actions/live-chat';
import { closeWidget } from 'actions/main';
import { applyKustomerCoreFunctionWithCallback } from 'embed/kustomerCore';
import { applyZopimFunction } from 'embed/zopim';
import { useAppDispatch, useAppSelector } from 'hooks';
import { clearConversation } from 'persistence/useClearConversationOnTimeout';
import { clearSmoochSession } from 'persistence/utils';
import { useCallback } from 'react';
import { selectWidgetInitMethod } from 'reducers/config';
import {
  selectLiveChatIntegration,
  selectChatId,
  selectIntegrationConfig,
  selectLiveChatStatus,
  selectUserEndedChat,
  setUserEndedChat,
} from 'reducers/live-chat';
import {
  addScreenReaderNotification,
  selectConversationId,
  selectIsSolveConfigPreview,
} from 'reducers/workflows';
import Smooch from 'smooch';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';

export const useHandleEndLiveChat = ({
  shouldResolveConversation = true,
  shouldSendUserLeftMessage = true,
}: {
  shouldResolveConversation?: boolean;
  /**
   * Used for Sunco only
   */
  shouldSendUserLeftMessage?: boolean;
} = {}) => {
  const dispatch = useAppDispatch();
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const chatId = useAppSelector(selectChatId);
  const { integrationId } = useAppSelector(selectIntegrationConfig);
  const liveChatStatus = useAppSelector(selectLiveChatStatus);
  const isSolveConfigPreview = useAppSelector(selectIsSolveConfigPreview);
  const userEndedChat = useAppSelector(selectUserEndedChat);
  const conversationId = useAppSelector(selectConversationId);
  const widgetInitMethod = useAppSelector(selectWidgetInitMethod);
  const userLeftMessage = 'User left the chat';

  const handleEndLiveChat = useCallback(async () => {
    let shouldCloseWidget = true;

    switch (liveChatIntegration) {
      case 'sunco':
        if (shouldSendUserLeftMessage) {
          Smooch.sendMessage(userLeftMessage, chatId);
        }
        // Need timeout to allow userLeftMessage request to be sent
        setTimeout(() => {
          dispatch(clearConversation());
          clearSmoochSession(integrationId);
          Smooch.destroy();
        }, 400);
        break;
      case 'zendesk':
        /**
         * when zendesk agent leaves it is set to inactive, sending another message
         * would start a new zendesk chat
         */
        if (liveChatStatus === 'inactive') {
          applyZopimFunction(widgetInitMethod, 'endChat', {
            clear_dept_id_on_chat_ended: true,
          });
          dispatch(clearConversation());
        } else {
          applyZopimFunction(widgetInitMethod, 'endChat', {
            clear_dept_id_on_chat_ended: true,
          });
          await dispatch(
            sendUserReply({
              addWidgetComponent: false,
              message: userLeftMessage,
            }),
          );
          dispatch(clearConversation());
        }
        break;
      case 'kustomer':
        try {
          await applyKustomerCoreFunctionWithCallback('endConversation', {
            conversationId: chatId,
          });
        } catch (err) {
          sendErrorToSentry({
            additionalContext: { chatId },
            conversationId: conversationId,
            error: `Failed to end Kustomer One Chat conversation -- ${err}`,
          });
          dispatch(clearConversation());
          break;
        }

        if (userEndedChat || liveChatStatus === 'inactive') {
          /**
           * conversation was already ended by the user once meaning
           * we called endConversation or we already received the
           * onConversationEnd event so we don't need to wait
           * for the onSatifactionReceived event.
           */
          dispatch(clearConversation());
        } else {
          /**
           * conversation was ended in the above try block but we don't
           * want to close the widget to allow the onSatisfactionReceived
           * event to be triggered.
           */
          await dispatch(
            sendUserReply({
              addWidgetComponent: false,
              message: userLeftMessage,
            }),
          );
          shouldCloseWidget = false;
        }
        break;
      case 'freshchat':
        if (shouldResolveConversation) {
          await dispatch(resolveConversation());
        }
        dispatch(clearConversation());
        break;

      default:
        await dispatch(sendUserReply({ message: userLeftMessage }));
        dispatch(clearConversation());
        break;
    }

    if (shouldCloseWidget && !isSolveConfigPreview) {
      // close the widget after we end the live chat
      dispatch(closeWidget());
    }

    // setUserEndedChat would trigger a re-run of this hook, which interferes with sunco sending
    // "User left the chat" before we clear the conversation, hence don't set for sunco
    if (liveChatIntegration !== 'sunco') {
      dispatch(setUserEndedChat(true));
    }
    dispatch(addScreenReaderNotification('Live chat ended'));
  }, [
    liveChatIntegration,
    isSolveConfigPreview,
    dispatch,
    shouldSendUserLeftMessage,
    liveChatStatus,
    userEndedChat,
    shouldResolveConversation,
    chatId,
    integrationId,
    widgetInitMethod,
    conversationId,
  ]);

  return handleEndLiveChat;
};
